import { AuthService, AuthTokenStorageService } from '@/lib/auth';
import axios, { type AxiosInstance } from 'axios';
import {
  createObjectResponseInterceptor,
  isUnauthorizedError,
} from '@/lib/api/errors';

const apiURL = process.env.NEXT_PUBLIC_APP_API_URL;
export const hrApiURL = process.env.NEXT_PUBLIC_APP_HR_SITE_API;

export const apiInstitute =
  process.env.NEXT_PUBLIC_INSTITUTE_API_URL ??
  'https://www.institute.gov.mn/api';

type ClientOptions = {
  authMode?: 'static' | 'request';
  logoutOnUnauthorized?: boolean;
  logServerError?: boolean;
  timeout?: number;
  withCredentials?: boolean;
};

function attachResponseValidation(
  client: AxiosInstance,
  options?: Pick<ClientOptions, 'logoutOnUnauthorized' | 'logServerError'>
): void {
  const { onFulfilled } = createObjectResponseInterceptor();

  client.interceptors.response.use(onFulfilled, async (error) => {
    if (options?.logoutOnUnauthorized && isUnauthorizedError(error)) {
      AuthService.logout();
    }

    if (options?.logServerError) {
      console.error('Server API request failed:', error);
    }

    return Promise.reject(error);
  });
}

function attachAuth(
  client: AxiosInstance,
  mode: ClientOptions['authMode'] = 'static'
): void {
  if (mode === 'request') {
    client.interceptors.request.use(
      (config) => {
        const accessToken = AuthTokenStorageService.getAccessToken();
        if (accessToken) {
          config.headers.Authorization = `Bearer ${accessToken}`;
        }
        return config;
      },
      (error) => Promise.reject(error)
    );
    return;
  }

  const accessToken = AuthTokenStorageService.getAccessToken();
  if (accessToken) {
    client.defaults.headers.common.Authorization = `Bearer ${accessToken}`;
  }
}

function createApiClient(
  baseURL: string | undefined,
  options: ClientOptions = {}
) {
  const client = axios.create({
    baseURL,
    withCredentials: options.withCredentials ?? true,
    timeout: options.timeout ?? 90000,
  });

  attachAuth(client, options.authMode);
  attachResponseValidation(client, {
    logoutOnUnauthorized: options.logoutOnUnauthorized,
    logServerError: options.logServerError,
  });

  return client;
}

export const apiClient = createApiClient(apiURL, {
  logoutOnUnauthorized: true,
});

export const hrApiClient = createApiClient(hrApiURL, {
  authMode: 'request',
});
